import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockInvoices } from '@/mocks/invoices';
import { mockPartners } from '@/mocks/partners';
import { Plus, FileText, Eye, CheckCircle, Search } from 'lucide-react';

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let result = [...mockInvoices];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i => {
        const partner = mockPartners.find(p => p.id === i.partner_id);
        return i.invoice_number?.toLowerCase().includes(q) || partner?.display_name.toLowerCase().includes(q);
      });
    }
    return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [search]);

  const statusBadge = (status: string) => {
    switch (status) {
      case 'paid': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'sent': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      case 'draft': return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
      case 'overdue': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      case 'void': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Invoices</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filtered.length} invoices</p>
        </div>
        <button
          onClick={() => navigate('/finance/invoices/new')}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl transition-all whitespace-nowrap"
          style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
        >
          <Plus size={16} />
          Generate
        </button>
      </div>

      <div className="relative max-w-md group">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          placeholder="Search invoice number or partner..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm rounded-xl outline-none transition-all"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
        />
      </div>

      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="overflow-x-auto table-scroll">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Invoice #</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Partner</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Period</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Currency</th>
                <th className="text-right px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Total</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Tax</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}>Due</th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'var(--text-muted)' }}></th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
              {filtered.map(i => {
                const partner = mockPartners.find(p => p.id === i.partner_id);
                return (
                  <tr key={i.id} className="cursor-pointer transition-colors hover-surface" style={{ borderColor: 'var(--border-separator)' }} onClick={() => navigate(`/finance/invoices/${i.id}`)}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-secondary)' }}>{i.invoice_number}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-tertiary)' }}>{partner?.display_name}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{i.period_start} — {i.period_end}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{i.currency}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>{i.total.toLocaleString()}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{i.tax_rate}%</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border" style={statusBadge(i.status)}>
                        {i.status}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{i.due_date || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button className="p-1.5 rounded-lg transition-colors btn-ghost">
                          <Eye size={14} />
                        </button>
                        {i.status === 'sent' && (
                          <button className="p-1.5 rounded-lg transition-colors hover-success-bg" style={{ color: 'var(--success-text)' }}>
                            <CheckCircle size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}